import { BehaviorSubject } from 'rxjs';
import axios from 'axios';
import Cookies from 'js-cookie';
import { bootstrapRefreshCookie, revokeRefreshToken } from './services/tasService';
import TokenManagerService from './services/TokenManagerService';

const loginSubject = new BehaviorSubject();
const resetTimer = new BehaviorSubject();
const userEmail = new BehaviorSubject();

/**
 * Fetches the caller's current user profile straight from the server,
 * bypassing any cached copy. Used to re-verify security-sensitive fields
 * (e.g. role) that must never be trusted from a client-writable store like
 * localStorage, which can be tampered with via devtools or a response-
 * rewriting proxy.
 *
 * @param {string} accessToken
 * @returns {Promise<object>} the verified `details` object (role, customerId, etc.)
 */
export const fetchCurrentUserDetails = (accessToken) => {
    return axios
        .get(process.env.REACT_APP_API_URL + 'session/userDetails', {
            silent: true,
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { token: accessToken },
        })
        .then((res) => {
            const userData = res.data;
            const innerData = userData.details?.data || userData.data || {};
            return innerData.details || {};
        });
};

export class authService {
    constructor() {
        // If we still have an in-memory token (e.g. hot-reload), treat as logged-in.
        // Otherwise check localStorage for non-sensitive user profile data from a
        // previous session — note the token itself is NOT in localStorage.
        if (TokenManagerService.getAccessToken()) {
            let storedUser = localStorage.getItem('FOM_user');
            if (storedUser) loginSubject.next({ log: JSON.parse(storedUser) });
        }
    }

    /** Setting user as logged
     * @param {object} user - User data need to be logged.
     */
    setLoggedin = (user) => {
        this._loggingOut = false;
        //Convert session length minutes to milli seconds
        const sessionLength = user.userdata.sessionLength * (60 * 1000);
        localStorage.setItem('FOM_user_session_length', sessionLength);
        // Store the token in memory only — never in localStorage
        TokenManagerService.setTokens({ access: user.userdata.token });
        // Store non-sensitive user profile (without token) in localStorage for
        // cross-tab profile display and session-length timer
        const profileOnly = {
            ...user,
            userdata: { ...user.userdata, token: undefined },
        };
        localStorage.setItem('FOM_user', JSON.stringify(profileOnly));
        loginSubject.next({ log: user });
    };

    /**
     * Called after SAML redirect-back to bootstrap the Vista session.
     *
     * The SAML callback lands on the STM host, so the httpOnly refresh_token
     * cookie is scoped there and invisible on the Vista domain. To work around
     * this we:
     *   1. Call GET /refresh with the short-lived sso_jwt as a Bearer token.
     *      TAS issues a new refresh_token cookie on the Vista domain.
     *   2. Call POST /refresh (cookie-based) to rotate the token and receive
     *      a fresh access JWT.
     *   3. Clean up the sso_jwt cookie.
     *
     * @param {string} ssoJwt - The sso_jwt value read from the cookie.
     */
    loginFromRefreshToken = (ssoJwt) => {
        return bootstrapRefreshCookie(ssoJwt)
            .then(() => {
                // Remove sso_jwt immediately — it's no longer needed after the
                // refresh_token cookie has been bootstrapped on the Vista domain.
                Cookies.remove('sso_jwt');
                return TokenManagerService.refreshAccessToken();
            })
            .then((accessToken) => {
                if (!accessToken) {
                    return this.setLogout();
                }
                // Store the token in memory
                TokenManagerService.setTokens({ access: accessToken });
                return this._hydrateSession(accessToken);
            })
            .catch((e) => {
                console.log('loginFromRefreshToken failed:', e);
                Cookies.remove('sso_jwt');
                this.setLogout();
            });
    };

    /**
     * Silent refresh on page reload — attempts POST /refresh using the
     * httpOnly refresh_token cookie (no sso_jwt needed).
     */
    silentRefresh = () => {
        return TokenManagerService.refreshAccessToken()
            .then((accessToken) => {
                if (!accessToken) return;
                TokenManagerService.setTokens({ access: accessToken });
                return this._hydrateSession(accessToken);
            })
            .catch((e) => {
                console.log('silentRefresh failed:', e);
            });
    };

    /**
     * Fetches Vista user profile and stores session data.
     */
    _hydrateSession = (accessToken) => {
        return axios
            .get(process.env.REACT_APP_API_URL + 'session/userDetails', {
                silent: true,
                headers: { Authorization: `Bearer ${accessToken}` },
                params: { token: accessToken },
            })
            .then((res) => {
                const userData = res.data;
                const innerData = userData.details?.data || userData.data || {};
                const details = innerData.details || {};
                const sessionLength = innerData.sessionLength || 30;

                localStorage.setItem('FOM_user_session_length', sessionLength * (60 * 1000));
                localStorage.setItem(
                    'FOM_user',
                    JSON.stringify({ userdata: { sessionLength, details } }),
                );
                this._loggingOut = false;
                loginSubject.next({ log: { userdata: { details, sessionLength } } });
            });
    };

    /**
     * Logging out user
     */
    setLogout = () => {
        // Guard against re-entrant calls. clearTokens() notifies listeners,
        // which may call setLogout() again → infinite recursion.
        if (this._loggingOut) return;
        this._loggingOut = true;

        if (window.walkme) {
            window.walkme = {
                userId: undefined,
                email: undefined,
                fullName: undefined,
            };
        }
        // Best-effort revoke the TAS refresh token cookie
        revokeRefreshToken();
        Cookies.remove('sso_jwt');

        TokenManagerService.markLoggedOut();
        TokenManagerService.clearTokens();
        localStorage.removeItem('FOM_user');
        localStorage.removeItem('FOM_master_model');
        localStorage.removeItem('FOM_user_session_length');
        loginSubject.next();
    };

    /**
     * Get Login status as behaviour subject
     */
    getLoginStatus = () => {
        return loginSubject.asObservable();
    };

    /**
     * Request reset timer when api call
     */
    requestResetTimer() {
        resetTimer.next(true);
    }

    /**
     * Get Login status as behaviour subject
     */
    getUserEmail = () => {
        return userEmail.asObservable();
    };

    /**
     * Set timer
     */
    setResetTimer = () => {
        return resetTimer.asObservable();
    };
}
